// App shell: a segmented control between the three fluid panes (Upload / Review /
// Edit), navigable anytime, each showing a count. CONTRACTS §10, DESIGN §9.

import { useState } from "react";
import { CONFIG_OK, missingConfig } from "./config";
import { useArtifacts } from "./useArtifacts";
import { UploadPane } from "./panes/UploadPane";
import { ReviewPane } from "./panes/ReviewPane";
import { EditPane } from "./panes/EditPane";

type Pane = "upload" | "review" | "edit";

export function App() {
  const [pane, setPane] = useState<Pane>("upload");
  const store = useArtifacts();

  const tabs: { id: Pane; label: string; count: number; hint: string }[] = [
    { id: "upload", label: "Upload", count: store.counts.pending, hint: "in flight" },
    { id: "review", label: "Review", count: store.counts.needs_review, hint: "awaiting review" },
    { id: "edit", label: "Edit", count: store.counts.confirmed, hint: "confirmed" },
  ];

  return (
    <div className="app">
      <header className="app__header">
        <div className="app__title">
          <span className="app__title-main">NGA Ingest</span>
          <span className="app__title-sub">capture · review · edit</span>
        </div>
        <nav className="segmented" role="tablist" aria-label="Panes">
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={pane === t.id}
              className={"segmented__btn" + (pane === t.id ? " segmented__btn--active" : "")}
              onClick={() => setPane(t.id)}
            >
              <span className="segmented__label">{t.label}</span>
              <span className="segmented__count" title={t.hint}>
                {store.loading && t.count === 0 ? "…" : t.count}
              </span>
            </button>
          ))}
        </nav>
        <button
          type="button"
          className="btn btn--ghost app__refresh"
          onClick={() => void store.refresh()}
          title="Reload from server"
          disabled={store.loading}
        >
          {store.loading ? "↻…" : "↻"}
        </button>
      </header>

      {!CONFIG_OK && (
        <div className="banner banner--warn">
          Missing build-time config: <code>{missingConfig().join(", ")}</code>. Set them in{" "}
          <code>.env</code> (see <code>.env.example</code>) and rebuild.
        </div>
      )}
      {store.error && (
        <div className="banner banner--error">
          {store.error}{" "}
          <button type="button" className="btn btn--tiny" onClick={() => void store.refresh()}>
            Retry
          </button>
        </div>
      )}

      <main className="app__main">
        {/* The Upload pane stays mounted (hidden when inactive) so its session
            strip and any queue of dropped files keep going while you check
            Review; it releases the camera whenever it isn't showing. The other
            panes render only while active. */}
        <UploadPane
          active={pane === "upload"}
          onArtifactSettled={store.upsert}
          onArtifactRemoved={store.remove}
        />
        {pane === "review" && (
          <ReviewPane
            items={store.byStatus.needs_review}
            pendingItems={store.byStatus.pending}
            loading={store.loading}
            existingTourTags={store.tourTags}
            onChanged={() => void store.refresh()}
            onUpsert={store.upsert}
            onRemove={store.remove}
          />
        )}
        {pane === "edit" && (
          <EditPane
            items={store.byStatus.confirmed}
            loading={store.loading}
            onChanged={() => void store.refresh()}
            onUpsert={store.upsert}
            onRemove={store.remove}
          />
        )}
      </main>
    </div>
  );
}
